import { PhoneCallbackOutlined } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { usePhoneCalls } from '../../../contexts/PhoneCallsContext/PhoneCallsContext';
import useDebounce from '../../../hooks/useDebounce';
import {
  CallDirection,
  CallLog,
  CallLogStatus,
  Conference,
  FindAllCallsParams,
  SimplifiedMember,
} from '../../../interfaces/calls';
import { Pagination as PaginationType } from '../../../interfaces/common';
import voiceService from '../../../services/appointments/calls';
import coreService from '../../../services/core';
import { PhoneOutgoing } from '../../common/PhoneOutgoing';
import { ClinicFilter } from '../ClinicFilter';
import AgentsFilter from '../filters/AgentsFilter';
import { ConferenceDetailDrawer } from './ConferenceDetailDrawer/ConferenceDetailDrawer';
import './phone-calls-center.css';

type Props = {
  breadcrumbs: ReactNode;
};

const INITIAL_PAGINATION_STATE = {
  currentPage: 1,
  perPage: 10,
  totalCount: 0,
  totalPages: 0,
};

export const CallsCenter = ({ breadcrumbs }: Props) => {
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<FindAllCallsParams>({});
  const [pagination, setPagination] = useState<PaginationType>(INITIAL_PAGINATION_STATE);
  const [selectedConference, setSelectedConference] = useState<Conference | null>(null);
  const [members, setMembers] = useState<SimplifiedMember[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [playingCallId, setPlayingCallId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const notify = useNotify();

  const { agent } = usePhoneCalls();

  const fetchCallHistory = useCallback(async () => {
    try {
      setLoading(true);

      const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          if (Array.isArray(value) && value.length === 0) {
            return acc;
          }
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await voiceService.searchConferences({
        page: page,
        pageSize: pagination.perPage,
        ...cleanedFilters,
      });

      if (!isMountedRef.current) {
        return;
      }

      if (!response.data || !response.pagination) {
        notify('Failed to fetch call history', 'error');
        return;
      }

      setConferences(response.data);
      setPagination(response.pagination);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      notify('Failed to fetch call history', 'error');
    }

    if (isMountedRef.current) {
      setLoading(false);
    }
  }, [filters, pagination.perPage, notify, page]);

  const handlePlayConferenceRecording = useCallback(
    (conference: Conference) => {
      if (!conference.recordingUrl) {
        notify('No recording available', 'warning');
        return;
      }

      // Check if clicking on the same recording that's currently playing
      const isSameRecording = playingCallId === conference.conferenceId;

      if (isSameRecording && audioRef.current) {
        // Stop playing
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        setPlayingCallId(null);
        return;
      }

      // Stop any currently playing audio (different recording)
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Start playing new audio
      const audio = new Audio(conference.recordingUrl);
      audioRef.current = audio;

      audio.onended = () => {
        audioRef.current = null;
        setPlayingCallId(null);
      };

      audio.onerror = () => {
        notify('Error playing recording', 'error');
        audioRef.current = null;
        setPlayingCallId(null);
      };

      // Set state and start playing
      setPlayingCallId(conference.conferenceId);

      audio.play().catch(() => {
        notify('Error playing recording', 'error');
        audioRef.current = null;
        setPlayingCallId(null);
      });
    },
    [playingCallId, notify],
  );

  const handleFilterChanges = (filter?: {
    [filter: string]: boolean | number | string | undefined | string[] | number[];
  }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };

    if (updatedFilters.startDate === '') {
      delete updatedFilters.startDate;
    }
    if (updatedFilters.endDate === '') {
      delete updatedFilters.endDate;
    }

    setFilters(updatedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleRefresh = useCallback(() => {
    setPage(1);
    fetchCallHistory();
  }, [fetchCallHistory]);

  const searchUsersByTerm = useCallback(
    async ({
      searchTerm,
      target,
    }: {
      searchTerm?: string | null;
      target: 'providers' | 'members';
    }) => {
      if (!searchTerm) {
        return [];
      }
      const { data } = await coreService.fetchMembers({ searchTerm, target });
      return data.map((member) => ({
        id: member.id,
        phone: member.phone,
        name: member.fullName,
      }));
    },
    [],
  );

  const debouncedFetchMembers = useDebounce(
    useCallback(
      async (param: { searchTerm: string }) => {
        setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
      },
      [searchUsersByTerm],
    ),
  );

  // Refetch when filters change
  useEffect(() => {
    isMountedRef.current = true;
    fetchCallHistory();

    return () => {
      isMountedRef.current = false;
    };
  }, [filters, fetchCallHistory, page]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  // Render
  return (
    <Box className='tab-section calls-center-section' marginTop={0}>
      <Box className='calls-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
          <Box display='flex' alignItems='center' gap={2}>
            {agent && !agent?.trackingNumbers?.incoming?.length && (
              <Chip label='Please link a phone number to your account to enable calling and receiving calls' />
            )}
          </Box>
        </Box>

        <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={3} flexWrap='wrap'>
          {/* Member Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Member</Typography>
            <Autocomplete
              fullWidth
              className='member-selector'
              options={members}
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.name} ID: ${option.id}`}
              onChange={(_, v) => handleFilterChanges({ coreMemberId: v?.id || undefined })}
              noOptionsText='No members found'
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={members.find((m) => m.id === filters.coreMemberId) || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  placeholder='Enter a member name'
                  onChange={(e) => debouncedFetchMembers({ searchTerm: e.target.value })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Avatar sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                    {`${option.name?.[0] ?? ''}${option.name?.[1] ?? ''}`.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography fontSize={'0.8rem'}>{option.name}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Agent Filter */}
          <AgentsFilter
            value={filters.coreProviderId}
            filterType='coreProviderId'
            handleFilterChanges={handleFilterChanges}
          />

          {/* From Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>From</Typography>
            <TextField
              type='text'
              value={filters.from || ''}
              size='small'
              placeholder='(555) 555-5555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ from: e.target.value })}
            />
          </Box>

          {/* To Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>To</Typography>
            <TextField
              type='text'
              value={filters.to || ''}
              size='small'
              placeholder='(555) 555-5555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ to: e.target.value })}
            />
          </Box>

          {/* Direction Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Direction</Typography>
            <Autocomplete
              size='small'
              options={Object.values(CallDirection)}
              getOptionLabel={(option) =>
                option === CallDirection.INBOUND ? 'Incoming' : 'Outgoing'
              }
              onChange={(_, value) => handleFilterChanges({ direction: value || undefined })}
              renderInput={(params) => (
                <TextField {...params} placeholder='All Calls' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Status Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Status</Typography>
            <Autocomplete
              size='small'
              multiple
              options={Object.values(CallLogStatus)}
              getOptionLabel={(option) => option.replace('_', ' ')}
              onChange={(_, value) => handleFilterChanges({ status: value })}
              renderInput={(params) => (
                <TextField {...params} placeholder='All status' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Created At */}
          <Box display='flex' flexDirection='column' alignItems='flex-start'>
            <Typography>Created At</Typography>
            <Box display='flex' alignItems='flex-start' gap={2}>
              <TextField
                size='small'
                type='date'
                label='From Date'
                value={filters.startDate || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChanges({ startDate: value ?? undefined });
                }}
              />
              <TextField
                size='small'
                type='date'
                label='Until Date'
                value={filters.endDate || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChanges({ endDate: value || undefined });
                }}
              />
            </Box>
          </Box>

          {/* Clinic Filter */}
          <ClinicFilter
            selectedClinicIds={filters.clinicIds || []}
            includeUnassigned={!!filters.includeUnassigned}
            onChange={({ clinicIds, includeUnassigned }) =>
              handleFilterChanges({ clinicIds, includeUnassigned })
            }
          />

          {/* Refresh */}
          <Box display='flex' alignSelf='center' justifyContent='center' marginBottom={2}>
            <RefreshIcon onClick={handleRefresh} sx={{ cursor: 'pointer' }} />
          </Box>
        </Box>
      </Box>

      {/* Column Headers */}
      {!loading && conferences.length > 0 && (
        <Box
          display='flex'
          alignItems='center'
          sx={{
            padding: '12px 16px',
            marginTop: 2,
            marginBottom: 1,
            fontWeight: 'bold',
          }}>
          <Box flex='0 0 40px' minWidth='40px' px={1}>
            <Typography variant='body2' fontWeight='bold'></Typography>
          </Box>
          <Box flex='1 1 180px' minWidth='180px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              From
            </Typography>
          </Box>
          <Box flex='1 1 180px' minWidth='180px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              To
            </Typography>
          </Box>
          <Box flex='0 0 100px' minWidth='100px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              Duration
            </Typography>
          </Box>
          <Box flex='0 0 140px' minWidth='140px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              Date/Time
            </Typography>
          </Box>
          <Box flex='0 0 120px' minWidth='120px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              Resources
            </Typography>
          </Box>
          <Box flex='1 1 200px' minWidth='200px' px={1}>
            <Typography variant='body2' fontWeight='bold'>
              Notes
            </Typography>
          </Box>
        </Box>
      )}

      {/* Conference Rows */}
      <Box>
        {loading ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <Typography>Loading conferences...</Typography>
          </Box>
        ) : conferences.length === 0 ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <Typography color='textSecondary'>No registered conferences</Typography>
          </Box>
        ) : (
          conferences.map((conference) => {
            const firstCall = conference.calls[0];
            const formatDuration = (duration: number | null) => {
              if (!duration) return '00:00';
              const minutes = Math.floor(duration / 60);
              const seconds = duration % 60;
              return `${minutes.toString().padStart(2, '0')}:${seconds
                .toString()
                .padStart(2, '0')}`;
            };

            const getLatestNote = () => {
              if (conference.notes && conference.notes.length > 0) {
                return conference.notes[0].content;
              }
              return null;
            };

            const getCallIcon = (call: CallLog) => {
              if (call.direction === CallDirection.INBOUND) {
                return <PhoneCallbackOutlined sx={{ color: '#AF77BE', fontSize: 20 }} />;
              }
              if (call.direction === CallDirection.OUTBOUND) {
                return <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />;
              }

              return <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />;
            };

            return (
              <Box
                key={conference.conferenceId}
                display='flex'
                alignItems='center'
                sx={{
                  padding: '12px 16px',
                  marginBottom: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
                onClick={() => setSelectedConference(conference)}>
                <Box flex='0 0 40px' minWidth='40px' px={1}>
                  {firstCall && getCallIcon(firstCall)}
                </Box>
                <Box flex='1 1 180px' minWidth='180px' px={1}>
                  <Typography variant='body2' fontWeight={500}>
                    {firstCall?.caller?.name || 'Unknown'}
                  </Typography>
                  {firstCall?.caller?.id && (
                    <Typography variant='caption' color='textSecondary' display='block'>
                      ID: {firstCall.caller.id}
                    </Typography>
                  )}
                  <Typography variant='caption' color='textSecondary'>
                    {firstCall?.caller?.isFromBrowser
                      ? 'Browser'
                      : firstCall?.caller?.phoneNumber || ''}
                  </Typography>
                </Box>
                <Box flex='1 1 180px' minWidth='180px' px={1}>
                  <Typography variant='body2' fontWeight={500}>
                    {firstCall?.callee?.name || 'Unknown'}
                  </Typography>
                  {firstCall?.callee?.id && (
                    <Typography variant='caption' color='textSecondary' display='block'>
                      ID: {firstCall.callee.id}
                    </Typography>
                  )}
                  <Typography variant='caption' color='textSecondary'>
                    {firstCall?.callee?.isToBrowser
                      ? 'Browser'
                      : firstCall?.callee?.phoneNumber || ''}
                  </Typography>
                </Box>
                <Box flex='0 0 100px' minWidth='100px' px={1}>
                  <Typography variant='body2'>{formatDuration(conference.duration)}</Typography>
                </Box>
                <Box flex='0 0 140px' minWidth='140px' px={1}>
                  <Typography variant='body2'>
                    {new Date(conference.createdAt).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric',
                    })}
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    {new Date(conference.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                    PT
                  </Typography>
                </Box>
                <Box flex='0 0 120px' minWidth='120px' px={1}>
                  {conference.recordingUrl && (
                    <Box
                      className={`voicemail-mini-player ${
                        playingCallId === conference.conferenceId ? 'playing' : 'idle'
                      }`}
                      onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size='small'
                        sx={{ padding: '4px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayConferenceRecording(conference);
                        }}>
                        {playingCallId === conference.conferenceId ? (
                          <StopIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <PlayArrowIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                      <Box className='voicemail-mini-player-separator'></Box>
                      <Typography variant='body2' sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {formatDuration(conference.recordingDuration)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box flex='1 1 200px' minWidth='200px' px={1}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {getLatestNote() || '-'}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Pagination */}
      {conferences.length > 0 && (
        <Box display='flex' justifyContent='flex-end' mt={2}>
          <TablePagination
            component='div'
            count={pagination.totalCount}
            page={pagination.currentPage - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[pagination.perPage]}
            labelRowsPerPage='Rows per page:'
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </Box>
      )}

      {/* Conference Details Modal */}
      <ConferenceDetailDrawer
        open={!!selectedConference}
        conference={selectedConference}
        onClose={() => setSelectedConference(null)}
        onNoteAdded={(note) => {
          if (!selectedConference) {
            return;
          }

          const updatedConference = {
            ...selectedConference,
            notes: [note, ...selectedConference.notes],
          };

          setSelectedConference(updatedConference);

          setConferences((prevConferences) =>
            prevConferences.map((conf) =>
              conf.conferenceId === selectedConference.conferenceId ? updatedConference : conf,
            ),
          );
        }}
      />
    </Box>
  );
};
